import React, { useEffect, useMemo, useRef, useState } from "react";
import {
	BellOutlined,
	CheckCircleOutlined,
	ClockCircleOutlined,
	DownOutlined,
	TeamOutlined,
} from "@ant-design/icons";
import {
	Checkbox,
	InputNumber,
	Modal,
	Table,
	Typography,
	message,
} from "antd";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import useAxios from "../../utilities/useAxios";

dayjs.locale("ko");

const { Text } = Typography;

const WEEKDAY = ["일", "월", "화", "수", "목", "금", "토"];

const formatDateWithWeekday = (value) => {
	if (!value) return "-";
	const d = dayjs(value);
	if (!d.isValid()) return value;
	return `${d.format("YYYY-MM-DD")} (${WEEKDAY[d.day()]})`;
};

const formatAsOfLabel = (asOf) => {
	if (!asOf) return "";
	const d = dayjs(asOf);
	if (!d.isValid()) return `${asOf} 기준`;
	return `${d.format("YYYY-MM-DD")}(${WEEKDAY[d.day()]}) 기준`;
};

const formatShortDate = (value) => {
	if (!value) return "";
	const d = dayjs(value);
	if (!d.isValid()) return "";
	return d.format("MM-DD");
};

const formatSixPeriod = (start, end) => {
	if (!start || !end) return "-";
	const s = dayjs(start);
	const e = dayjs(end);
	if (!s.isValid() || !e.isValid()) return "-";
	return `${s.format("YYYY-MM-DD")} ~ ${e.format("MM-DD")}`;
};

const formatPeriod = (start, end) => {
	if (!start || !end) return "-";
	const s = dayjs(start);
	const e = dayjs(end);
	if (!s.isValid() || !e.isValid()) return "-";
	if (s.year() === e.year()) {
		return `${s.format("YYYY-MM-DD")} ~ ${e.format("MM-DD")}`;
	}
	return `${s.format("YYYY-MM-DD")} ~ ${e.format("YYYY-MM-DD")}`;
};

const daysUntil = (target, asOf) =>
	dayjs(target).startOf("day").diff(dayjs(asOf).startOf("day"), "day");

const formatDayCount = (value) => {
	const n = Number(value);
	if (!Number.isFinite(n)) return "0";
	const rounded = Math.round(n * 10) / 10;
	return Number.isInteger(rounded) ? String(rounded) : String(rounded);
};

const dayOrZero = (value) => {
	if (value == null || value === "") return 0;
	const n = Number(value);
	return Number.isFinite(n) ? n : 0;
};

const replyCovered = (useValue, carryValue) =>
	dayOrZero(useValue) + dayOrZero(carryValue);

const normalizeReplyDay = (value) => {
	if (value == null || value === "") return null;
	const n = Number(value);
	if (!Number.isFinite(n)) return null;
	return Math.round(n * 10) / 10;
};

const getReplyStatus = (useValue, carryValue, baseline) => {
	const covered = replyCovered(useValue, carryValue);
	const base = Number(baseline) || 0;
	if (covered <= 0) return "none";
	if (covered + 1e-9 >= base) return "full";
	return "partial";
};

const replyLabel = (status, useValue, carryValue, baseline) => {
	const covered = formatDayCount(replyCovered(useValue, carryValue));
	const base = formatDayCount(baseline);
	if (status === "full") return `전체 회신 ${covered}/${base}일`;
	if (status === "partial") return `일부 회신 ${covered}/${base}일`;
	return `미회신 ${covered}/${base}일`;
};

const ReplyBadge = ({ status, useValue, carryValue, baseline }) => (
	<span className={`leave-promo__reply leave-promo__reply--${status}`}>
		{replyLabel(status, useValue, carryValue, baseline)}
	</span>
);

const StepHead = ({ index, title, subtitle }) => (
	<div className="leave-promo__col-head">
		<div>
			{index} {title}
		</div>
		<div className="leave-promo__col-sub">{subtitle}</div>
	</div>
);

const formatDesignateDue = (deadline, asOf) => {
	if (!deadline) return "";
	const d = dayjs(deadline);
	if (!d.isValid()) return "";
	const n = daysUntil(deadline, asOf);
	const dday = n > 0 ? `D-${n}` : n < 0 ? `D+${Math.abs(n)}` : "D-Day";
	return `${d.format("MM.DD.")}까지 통보 · ${dday}`;
};

const ReplyPlanEditor = ({
	useValue,
	carryValue,
	baseline,
	onUseChange,
	onCarryChange,
	onCommit,
}) => {
	const base = Number(baseline) || 0;
	return (
		<div className="leave-promo__plan">
			<div className="leave-promo__plan-inputs">
				<span className="leave-promo__plan-field">
					사용
					<InputNumber
						size="small"
						min={0}
						max={base}
						step={0.5}
						value={useValue == null ? 0 : useValue}
						onChange={onUseChange}
						onBlur={onCommit}
						onPressEnter={(e) => e.target.blur()}
					/>
				</span>
				<span className="leave-promo__plan-field">
					이월
					<InputNumber
						size="small"
						min={0}
						max={Math.min(10, base)}
						step={0.5}
						value={carryValue == null ? 0 : carryValue}
						onChange={onCarryChange}
						onBlur={onCommit}
						onPressEnter={(e) => e.target.blur()}
					/>
				</span>
			</div>
			<ReplyBadge
				status={getReplyStatus(useValue, carryValue, baseline)}
				useValue={useValue}
				carryValue={carryValue}
				baseline={baseline}
			/>
		</div>
	);
};

const DdayBadge = ({ tone, label }) => (
	<span className={`leave-promo__dday leave-promo__dday--${tone}`}>
		{label}
	</span>
);

const sixDday = (row, asOf) => {
	const toStart = daysUntil(row.notice_six_start, asOf);
	if (toStart > 0) {
		return { tone: "orange", label: `시작 D-${toStart}` };
	}
	const toEnd = daysUntil(row.notice_six_end, asOf);
	if (toEnd > 0) return { tone: "orange", label: `마감 D-${toEnd}` };
	if (toEnd === 0) return { tone: "orange", label: "마감 D-Day" };
	return { tone: "orange", label: `마감 D+${Math.abs(toEnd)}` };
};

const twoDday = (row, asOf) => {
	const n = daysUntil(row.notice_two_deadline, asOf);
	if (n > 0) return { tone: "red", label: `마감 D-${n}` };
	if (n === 0) return { tone: "red", label: "마감 D-Day" };
	return { tone: "red", label: `마감 D+${Math.abs(n)}` };
};

const draftKey = (row, kind) => `${row.user_id}:${kind}`;

const PROMO_TABLE_SCROLL_Y = 440;

const CollapsiblePromoPanel = ({
	panelKey,
	expanded,
	onToggle,
	panelClassName,
	headLeft,
	rowCount,
	todo,
	children,
	footer,
}) => (
	<section
		className={`leave-promo__panel ${panelClassName}${
			expanded
				? " leave-promo__panel--expanded"
				: " leave-promo__panel--collapsed"
		}`}>
		<button
			type="button"
			className="leave-promo__panel-toggle"
			aria-expanded={expanded}
			aria-controls={`leave-promo-panel-${panelKey}`}
			onClick={() => onToggle(panelKey)}>
			<div className="leave-promo__panel-head">
				<div className="leave-promo__panel-left">{headLeft}</div>
				<div className="leave-promo__panel-right">
					<span className="leave-promo__count">
						{expanded
							? `${rowCount}명`
							: `총 ${rowCount}행`}
					</span>
					<DownOutlined
						className={`leave-promo__chevron${
							expanded ? " leave-promo__chevron--open" : ""
						}`}
						aria-hidden
					/>
				</div>
			</div>
		</button>
		{expanded ? (
			<div
				id={`leave-promo-panel-${panelKey}`}
				className="leave-promo__panel-body">
				<p className="leave-promo__todo">{todo}</p>
				<div className="leave-promo__table-wrap">{children}</div>
				{footer}
			</div>
		) : null}
	</section>
);

const LeavePromotionPanels = ({
	asOf,
	sixMonths,
	twoMonths,
	firstYearNine,
	firstYearTwo,
	onChanged,
}) => {
	const [drafts, setDrafts] = useState({});
	const draftsRef = useRef(drafts);
	const [excludedOpen, setExcludedOpen] = useState(false);
	const [panelOpen, setPanelOpen] = useState({
		fy9: false,
		fy2: false,
		six: false,
		two: false,
	});

	const togglePanel = (key) => {
		setPanelOpen((prev) => ({ ...prev, [key]: !prev[key] }));
	};

	useEffect(() => {
		draftsRef.current = drafts;
	}, [drafts]);

	useEffect(() => {
		const toDraft = (row) => ({
			use:
				row.reply_designated_days == null
					? null
					: Number(row.reply_designated_days),
			carry:
				row.reply_carryover_days == null
					? null
					: Number(row.reply_carryover_days),
		});
		const next = {};
		[...(sixMonths || []), ...(twoMonths || [])].forEach((row) => {
			next[draftKey(row, "6months")] = toDraft(row);
		});
		(firstYearNine || []).forEach((row) => {
			next[draftKey(row, "fy9")] = toDraft(row);
		});
		(firstYearTwo || []).forEach((row) => {
			next[draftKey(row, "fy2")] = toDraft(row);
		});
		draftsRef.current = next;
		setDrafts(next);
	}, [sixMonths, twoMonths, firstYearNine, firstYearTwo]);

	const sixRows = sixMonths || [];
	const twoWindow = twoMonths || [];
	const fyNineRows = firstYearNine || [];
	const fyTwoRows = firstYearTwo || [];

	const { visibleTwo, excludedTwo } = useMemo(() => {
		const visible = [];
		const excluded = [];
		twoWindow.forEach((row) => {
			const key = draftKey(row, "6months");
			const draft = drafts[key];
			const useDays =
				draft && draft.use !== undefined
					? draft.use
					: row.reply_designated_days;
			const carryDays =
				draft && draft.carry !== undefined
					? draft.carry
					: row.reply_carryover_days;
			const status = getReplyStatus(
				useDays,
				carryDays,
				row.remaining_days,
			);
			const next = { ...row, useDays, carryDays, status };
			if (status === "full") {
				excluded.push(next);
			} else {
				visible.push(next);
			}
		});
		return { visibleTwo: visible, excludedTwo: excluded };
	}, [twoWindow, drafts]);

	const showPanels =
		sixRows.length > 0 ||
		twoWindow.length > 0 ||
		fyNineRows.length > 0 ||
		fyTwoRows.length > 0;
	if (!showPanels) return null;

	const toggleNotice = async (row, kind, notified) => {
		try {
			await useAxios.post(
				`/utils/leave/annual-balance/${row.user_id}/reset-notice/`,
				{
					kind,
					notified,
					period_start: row.period_start,
				},
			);
			message.success(
				notified
					? "통보 완료로 표시했습니다."
					: "통보 표시를 해제했습니다.",
			);
			onChanged?.();
		} catch {
			message.error("통보 여부 저장에 실패했습니다.");
		}
	};

	const setDraftField = (key, field, value) => {
		setDrafts((current) => {
			const next = {
				...current,
				[key]: {
					...(current[key] || {}),
					[field]: value,
				},
			};
			draftsRef.current = next;
			return next;
		});
	};

	const saveReply = async (row, kind) => {
		const key = draftKey(row, kind);
		const draft = draftsRef.current[key] || {};
		const prevUse = normalizeReplyDay(row.reply_designated_days);
		const prevCarry = normalizeReplyDay(row.reply_carryover_days);
		const nextUse = normalizeReplyDay(draft.use);
		const nextCarry = normalizeReplyDay(draft.carry);
		if (prevUse === nextUse && prevCarry === nextCarry) return;
		const baseline = Number(row.remaining_days) || 0;
		if (
			(nextUse != null && nextUse < 0) ||
			(nextCarry != null && nextCarry < 0)
		) {
			message.error("회신 일수는 0 이상이어야 합니다.");
			return;
		}
		if (nextCarry != null && nextCarry > 10) {
			message.error("이월 일수는 최대 10일입니다.");
			return;
		}
		if (dayOrZero(nextUse) + dayOrZero(nextCarry) > baseline + 1e-9) {
			message.error(
				"사용 일수와 이월 일수의 합은 기준 일수를 넘을 수 없습니다.",
			);
			return;
		}
		try {
			await useAxios.post(
				`/utils/leave/annual-balance/${row.user_id}/reset-notice/`,
				{
					kind,
					period_start: row.period_start,
					designated_days: nextUse,
					carryover_days: nextCarry,
				},
			);
			message.success("사용신청서 회신을 저장했습니다.");
			onChanged?.();
		} catch (error) {
			message.error(
				error?.response?.data?.error ||
					"회신 일수 저장에 실패했습니다.",
			);
			const restored = {
				...draftsRef.current,
				[key]: { use: prevUse, carry: prevCarry },
			};
			draftsRef.current = restored;
			setDrafts(restored);
		}
	};

	const sixColumns = [
		{
			title: "교직원",
			dataIndex: "name",
			width: 100,
			render: (name) => <Text strong>{name}</Text>,
		},
		{
			title: "연차 초기화일",
			dataIndex: "reset_date",
			width: 150,
			render: formatDateWithWeekday,
		},
		{
			title: "통보 기한",
			key: "period",
			width: 170,
			render: (_, row) =>
				formatSixPeriod(row.notice_six_start, row.notice_six_end),
		},
		{
			title: "남은 기간",
			key: "dday",
			width: 110,
			render: (_, row) => {
				const badge = sixDday(row, asOf);
				return <DdayBadge tone={badge.tone} label={badge.label} />;
			},
		},
		{
			title: "미사용 연차",
			dataIndex: "remaining_days",
			width: 100,
			align: "right",
			render: (value) => `${value}일`,
		},
		{
			title: "통보",
			key: "notice",
			width: 100,
			render: (_, row) => (
				<span className="leave-promo__notice-cell">
					<Checkbox
						checked={Boolean(row.notified_six_months)}
						onChange={(e) =>
							toggleNotice(row, "6months", e.target.checked)
						}
					/>
					{row.notified_six_months && row.notified_six_months_at ? (
						<span className="leave-promo__notice-date">
							{formatShortDate(row.notified_six_months_at)}
						</span>
					) : null}
				</span>
			),
		},
		{
			title: "직원 회신",
			key: "reply",
			width: 280,
			render: (_, row) => {
				const key = draftKey(row, "6months");
				const draft = drafts[key] || {};
				const useValue =
					draft.use !== undefined
						? draft.use
						: row.reply_designated_days;
				const carryValue =
					draft.carry !== undefined
						? draft.carry
						: row.reply_carryover_days;
				return (
					<ReplyPlanEditor
						useValue={useValue}
						carryValue={carryValue}
						baseline={row.remaining_days}
						onUseChange={(value) =>
							setDraftField(key, "use", value)
						}
						onCarryChange={(value) =>
							setDraftField(key, "carry", value)
						}
						onCommit={() => saveReply(row, "6months")}
					/>
				);
			},
		},
	];

	const twoColumns = [
		{
			title: "교직원",
			dataIndex: "name",
			width: 100,
			render: (name) => <Text strong>{name}</Text>,
		},
		{
			title: "연차 초기화일",
			dataIndex: "reset_date",
			width: 150,
			render: formatDateWithWeekday,
		},
		{
			title: "통보 기한",
			dataIndex: "notice_two_deadline",
			width: 120,
			render: (value) =>
				value ? dayjs(value).format("YYYY-MM-DD") : "-",
		},
		{
			title: "남은 기간",
			key: "dday",
			width: 110,
			render: (_, row) => {
				const badge = twoDday(row, asOf);
				return <DdayBadge tone={badge.tone} label={badge.label} />;
			},
		},
		{
			title: "1차 회신",
			key: "reply1",
			width: 150,
			render: (_, row) => (
				<ReplyBadge
					status={row.status}
					useValue={row.useDays}
					carryValue={row.carryDays}
					baseline={row.remaining_days}
				/>
			),
		},
		{
			title: "2차 지정 일수",
			key: "secondDays",
			width: 110,
			align: "right",
			render: (_, row) => {
				const base = Number(row.remaining_days) || 0;
				const gap = Math.max(
					0,
					Math.round(
						(base - replyCovered(row.useDays, row.carryDays)) * 10,
					) / 10,
				);
				if (gap <= 0) return "해당 없음";
				return `${formatDayCount(gap)}일`;
			},
		},
		{
			title: "통보",
			key: "notice",
			width: 100,
			render: (_, row) => (
				<span className="leave-promo__notice-cell">
					<Checkbox
						checked={Boolean(row.notified_two_months)}
						onChange={(e) =>
							toggleNotice(row, "2months", e.target.checked)
						}
					/>
					{row.notified_two_months && row.notified_two_months_at ? (
						<span className="leave-promo__notice-date">
							{formatShortDate(row.notified_two_months_at)}
						</span>
					) : null}
				</span>
			),
		},
	];

	const buildFirstYearColumns = (kind) => {
		const replyHint =
			kind === "fy2" ? "촉구 후 5일 이내" : "촉구 후 10일 이내";
		return [
		{
			title: "교직원",
			dataIndex: "name",
			width: 100,
			render: (name) => <Text strong>{name}</Text>,
		},
		{
			title: "서면 촉구 기간",
			key: "notice_period",
			width: 180,
			render: (_, row) => formatPeriod(row.notice_start, row.notice_end),
		},
		{
			title: (
				<StepHead
					index="①"
					title="직원 회신"
					subtitle={replyHint}
				/>
			),
			key: "reply",
			width: 280,
			render: (_, row) => {
				const key = draftKey(row, kind);
				const draft = drafts[key] || {};
				const useValue =
					draft.use !== undefined
						? draft.use
						: row.reply_designated_days;
				const carryValue =
					draft.carry !== undefined
						? draft.carry
						: row.reply_carryover_days;
				return (
					<ReplyPlanEditor
						useValue={useValue}
						carryValue={carryValue}
						baseline={row.remaining_days}
						onUseChange={(value) =>
							setDraftField(key, "use", value)
						}
						onCarryChange={(value) =>
							setDraftField(key, "carry", value)
						}
						onCommit={() => saveReply(row, kind)}
					/>
				);
			},
		},
		{
			title: (
				<StepHead
					index="②"
					title="이월"
					subtitle="최대 10일 · 부서장 승인"
				/>
			),
			key: "carry",
			width: 170,
			render: (_, row) => {
				const key = draftKey(row, kind);
				const draft = drafts[key] || {};
				const carryValue =
					draft.carry !== undefined
						? draft.carry
						: row.reply_carryover_days;
				const carryDays = dayOrZero(carryValue);
				if (carryDays <= 0) {
					return <span className="leave-promo__dash">—</span>;
				}
				return (
					<div className="leave-promo__carry-cell">
						<div className="leave-promo__carry-amount">
							{formatDayCount(carryDays)}일 신청
						</div>
						<span className="leave-promo__carry-pending">
							부서장 승인 대기
						</span>
					</div>
				);
			},
		},
		{
			title: (
				<StepHead
					index="③"
					title="날짜 지정 통보"
					subtitle="기준 – 사용 계획 – 이월"
				/>
			),
			key: "designate",
			width: 230,
			render: (_, row) => {
				const key = draftKey(row, kind);
				const draft = drafts[key] || {};
				const useValue =
					draft.use !== undefined
						? draft.use
						: row.reply_designated_days;
				const carryValue =
					draft.carry !== undefined
						? draft.carry
						: row.reply_carryover_days;
				const baseline = Number(row.remaining_days) || 0;
				const covered = replyCovered(useValue, carryValue);
				const gap =
					Math.round(Math.max(0, baseline - covered) * 10) / 10;
				const coveredAll = covered + 1e-9 >= baseline;
				if (coveredAll) {
					const carryDays = dayOrZero(carryValue);
					return (
						<div className="leave-promo__designate-cell">
							<span className="leave-promo__na">해당 없음</span>
							{carryDays > 0 ? (
								<span className="leave-promo__carry-warn">
									이월 미승인 시 {formatDayCount(carryDays)}일
									지정 통보
								</span>
							) : null}
						</div>
					);
				}
				return (
					<div className="leave-promo__designate-cell">
						<span className="leave-promo__notice-cell">
							<Checkbox
								checked={Boolean(row.notified)}
								onChange={(e) =>
									toggleNotice(row, kind, e.target.checked)
								}
							/>
							<span className="leave-promo__designate-need">
								{formatDayCount(gap)}일 지정 필요
							</span>
							{row.notified && row.notified_at ? (
								<span className="leave-promo__notice-date">
									{formatShortDate(row.notified_at)}
								</span>
							) : null}
						</span>
						<span className="leave-promo__designate-due">
							{formatDesignateDue(row.designate_deadline, asOf)}
						</span>
					</div>
				);
			},
		},
	];
	};

	return (
		<div className="leave-promo">
			<div className="leave-promo__header">
				<div className="leave-promo__title">연차 사용촉진 대상자</div>
				<div className="leave-promo__asof">
					{formatAsOfLabel(asOf)}
					<span className="leave-promo__asof-note">
						{" "}
						(1년 미만은 입사 1주년 기준)
					</span>
				</div>
			</div>

			{fyNineRows.length > 0 ? (
				<CollapsiblePromoPanel
					panelKey="fy9"
					expanded={panelOpen.fy9}
					onToggle={togglePanel}
					panelClassName="leave-promo__panel--fy9"
					rowCount={fyNineRows.length}
					todo="할 일: 1년 만료 3개월 전부터 10일 안에 서면으로 촉구하고, 미회신 시 1개월 전까지 사용 시기를 지정해 통보"
					headLeft={
						<>
							<span className="leave-promo__icon leave-promo__icon--fy9">
								<TeamOutlined />
							</span>
							<span className="leave-promo__stage leave-promo__stage--fy9">
								1년미만
							</span>
							<span className="leave-promo__panel-title">
								연차 사용촉진 · 9일분 (입사 후 1~9개월 발생분)
							</span>
						</>
					}>
					<Table
						size="small"
						rowKey="user_id"
						pagination={false}
						columns={buildFirstYearColumns("fy9")}
						dataSource={fyNineRows}
						scroll={{ x: 1280, y: PROMO_TABLE_SCROLL_Y }}
					/>
				</CollapsiblePromoPanel>
			) : null}

			{fyTwoRows.length > 0 ? (
				<CollapsiblePromoPanel
					panelKey="fy2"
					expanded={panelOpen.fy2}
					onToggle={togglePanel}
					panelClassName="leave-promo__panel--fy2"
					rowCount={fyTwoRows.length}
					todo="할 일: 1년 만료 1개월 전부터 5일 안에 서면으로 촉구하고, 미회신 시 만료 10일 전까지 사용 시기를 지정해 통보"
					headLeft={
						<>
							<span className="leave-promo__icon leave-promo__icon--fy2">
								<TeamOutlined />
							</span>
							<span className="leave-promo__stage leave-promo__stage--fy2">
								1년미만
							</span>
							<span className="leave-promo__panel-title">
								연차 사용촉진 · 2일분 (입사 후 10~11개월 발생분)
							</span>
						</>
					}>
					<Table
						size="small"
						rowKey="user_id"
						pagination={false}
						columns={buildFirstYearColumns("fy2")}
						dataSource={fyTwoRows}
						scroll={{ x: 1280, y: PROMO_TABLE_SCROLL_Y }}
					/>
				</CollapsiblePromoPanel>
			) : null}

			{sixRows.length > 0 ? (
				<CollapsiblePromoPanel
					panelKey="six"
					expanded={panelOpen.six}
					onToggle={togglePanel}
					panelClassName="leave-promo__panel--six"
					rowCount={sixRows.length}
					todo="할 일: 미사용 연차 일수를 안내하고, 사용신청서(사용 시기)를 10일 안에 회신하도록 서면으로 요청"
					headLeft={
						<>
							<span className="leave-promo__icon leave-promo__icon--six">
								<BellOutlined />
							</span>
							<span className="leave-promo__stage leave-promo__stage--six">
								1차
							</span>
							<span className="leave-promo__panel-title">
								연차 사용촉진 · 연차 초기화 6개월 전
							</span>
						</>
					}>
					<Table
						size="small"
						rowKey="user_id"
						pagination={false}
						columns={sixColumns}
						dataSource={sixRows}
						scroll={{ x: 1000, y: PROMO_TABLE_SCROLL_Y }}
					/>
				</CollapsiblePromoPanel>
			) : null}

			{twoWindow.length > 0 ? (
				<CollapsiblePromoPanel
					panelKey="two"
					expanded={panelOpen.two}
					onToggle={togglePanel}
					panelClassName="leave-promo__panel--two"
					rowCount={visibleTwo.length}
					todo="할 일: 사용신청서를 회신하지 않았거나 일부만 지정한 교직원에게, 지정 안 된 일수의 사용 시기를 정해 서면으로 통보"
					footer={
						excludedTwo.length > 0 ? (
							<div className="leave-promo__excluded">
								<span className="leave-promo__excluded-text">
									<CheckCircleOutlined /> 1차 사용신청서 전체
									회신 {excludedTwo.length}명은 2차 대상에서
									자동 제외되었습니다.
								</span>
								<button
									type="button"
									className="leave-promo__excluded-link"
									onClick={() => setExcludedOpen(true)}>
									제외 명단 보기 &gt;
								</button>
							</div>
						) : null
					}
					headLeft={
						<>
							<span className="leave-promo__icon leave-promo__icon--two">
								<ClockCircleOutlined />
							</span>
							<span className="leave-promo__stage leave-promo__stage--two">
								2차
							</span>
							<span className="leave-promo__panel-title">
								연차 사용촉진 · 연차 초기화 2개월 전
							</span>
						</>
					}>
					<Table
						size="small"
						rowKey="user_id"
						pagination={false}
						columns={twoColumns}
						dataSource={visibleTwo}
						scroll={{ x: 900, y: PROMO_TABLE_SCROLL_Y }}
						locale={{
							emptyText: "표시할 2차 대상자가 없습니다.",
						}}
					/>
				</CollapsiblePromoPanel>
			) : null}

			<Modal
				title="2차 제외 명단"
				open={excludedOpen}
				onCancel={() => setExcludedOpen(false)}
				footer={null}
				destroyOnClose>
				<ul className="leave-promo__excluded-list">
					{excludedTwo.map((row) => (
						<li key={row.user_id}>
							{row.name} —{" "}
							{replyLabel(
								row.status,
								row.useDays,
								row.carryDays,
								row.remaining_days,
							)}
						</li>
					))}
				</ul>
			</Modal>
		</div>
	);
};

export default LeavePromotionPanels;
